import { Injectable, inject } from '@angular/core';
import {
  WatchQueryFetchPolicy,
  ApolloLink,
  ObservableQuery,
} from '@apollo/client';
import {
  FindAllGroupPermissionsRequest,
  FindUniqueGroupRolePermissionsRequest,
  Group,
  GroupMember,
  GroupPermission,
} from '@ih/app/shared/apis/interfaces';
import { Store } from '@ngxs/store';
import { Apollo, gql } from 'apollo-angular';
import { catchError, EMPTY, map, Observable, of, take } from 'rxjs';

@Injectable()
export class GroupsService {
  private readonly apollo = inject(Apollo);
  private readonly store = inject(Store);

  findUniqueGroup(groupId: string): Observable<Group | null> {
    type ResultType = {
      findUniqueGroup: Group | null;
    };

    let fetchPolicy = 'cache-only';

    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query findUniqueGroup($id: String!) {
        findUniqueGroup(request: { id: $id }) {
          id
          name
          slug
          description
          visibility
          photoURL
          photoRef
          organisationId
        }
      }
    `;

    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          id: groupId,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map(
          (
            result: ObservableQuery.Result<{ findUniqueGroup: Group | null }>,
          ) => {
            if (result.data && networkStatus) {
              this.apollo.client.cache.writeQuery({
                query,
                data: result.data,
                variables: {
                  id: groupId,
                },
              });
            }
            return (
              (result.data?.findUniqueGroup as Group | null | undefined) ?? null
            );
          },
        ),
        catchError((error) => {
          console.error(error);
          return EMPTY;
        }),
      );
  }

  findGroupMemberWithRolePermissions(
    userId: string,
    groupId: string,
  ): Observable<GroupMember[]> {
    type ResultType = { findMeGroupMember: GroupMember[] | null };

    let fetchPolicy = 'cache-only';

    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query FindMeGroupMember($userId: String!, $groupId: String!) {
        findMeGroupMember(
          request: { where: { userId: $userId, groupId: $groupId } }
        ) {
          role
          groupId
          groupRoleId
        }
      }
    `;

    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          userId,
          groupId,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map((result: ObservableQuery.Result<ResultType>) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
              variables: {
                userId,
                groupId,
              },
            });
          }

          return (
            (result.data?.findMeGroupMember as GroupMember[] | undefined) ?? []
          );
        }),
        catchError((error) => {
          console.error(error);
          return of([]);
        }),
      );
  }

  findAllGroupPermissions(
    request: FindAllGroupPermissionsRequest,
  ): Observable<GroupPermission[]> {
    type ResultType = { findAllGroupPermissions: GroupPermission[] | null };

    let fetchPolicy = 'cache-only';

    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query FindAllGroupPermissions($request: FindAllGroupPermissionsRequest!) {
        findAllGroupPermissions(request: $request) {
          id
          identifier
        }
      }
    `;

    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          request,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map((result: ObservableQuery.Result<ResultType>) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
              variables: {
                request,
              },
            });
          }

          return (
            (result.data?.findAllGroupPermissions as
              | GroupPermission[]
              | undefined) ?? []
          );
        }),
      );
  }

  findUniqueGroupRolePermissions(
    request: FindUniqueGroupRolePermissionsRequest,
  ): Observable<GroupPermission[]> {
    type ResultType = {
      findUniqueGroupRolePermissions: GroupPermission[] | null;
    };

    let fetchPolicy = 'cache-only';

    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query FindUniqueGroupRolePermissions(
        $request: FindUniqueGroupRolePermissionsRequest!
      ) {
        findUniqueGroupRolePermissions(request: $request) {
          id
          identifier
        }
      }
    `;

    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          request,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map((result: ObservableQuery.Result<ResultType>) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
              variables: {
                request,
              },
            });
          }

          return (
            (result.data?.findUniqueGroupRolePermissions as
              | GroupPermission[]
              | undefined) ?? []
          );
        }),
        catchError(() => of([])),
      );
  }
}
